import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import { LogoImage } from "../../components/Logo";
import { ThemeToggle } from "../../components/theme-toggle";
import { SUPPORTED_LANGUAGES, type Language } from "../../../../shared/const";
import { cn } from "../../lib/utils";

const LANG_LABEL: Record<Language, string> = {
  "zh-TW": "繁",
  "en-US": "EN",
  "zh-CN": "简",
};

export default function Login() {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const login = trpc.auth.login.useMutation({
    onSuccess: () => {
      refetch();
      setLocation("/");
    },
    onError: (e) => setError(e.message),
  });

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-[1.05fr_1fr]">
      {/* ── Brand panel ────────────────────────────────────── */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-border bg-card p-12 lg:flex">
        <div className="bg-grid bg-grid-fade absolute inset-0 opacity-50" />
        <div className="bg-dusk absolute inset-0" />
        <div className="bg-paper absolute inset-0" />

        <div className="relative z-10">
          <LogoImage className="h-8" />
        </div>

        <div className="relative z-10 max-w-md space-y-6">
          <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-warm">
            台 — 港 跨 境 旅 運
          </p>
          <h1 className="font-display text-[42px] font-light leading-[1.05] tracking-tight">
            一個平台
            <br />
            <span className="font-medium italic text-primary">串起兩岸三地</span>
            <br />
            的車輛調度。
          </h1>
          <p className="max-w-sm text-[13px] leading-relaxed text-muted-foreground">
            從用車需求建立到月結對帳，旅行社、車隊與調度員在同一個工作面上協作。多幣別、多語系、跨境結算。
          </p>

          <div className="flex flex-wrap gap-2 pt-2">
            {[
              { label: "TWD" },
              { label: "HKD" },
              { label: "USD" },
              { label: "繁中 / EN / 简中" },
            ].map((tag) => (
              <span
                key={tag.label}
                className="rounded-full border border-border bg-background/80 px-2.5 py-0.5 font-mono text-[12px] tracking-wide text-muted-foreground"
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-end justify-between text-[12px] text-muted-foreground">
          <div className="space-y-1">
            <p className="font-mono uppercase tracking-wider">v0.1 · MVP</p>
            <p>© 2026 Vehicle Console</p>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>SSO ready · TLS 1.3</span>
          </div>
        </div>
      </aside>

      {/* ── Form panel ────────────────────────────────────── */}
      <main className="relative flex flex-col px-6 py-8 md:px-12">
        {/* mobile brand row */}
        <div className="flex items-center justify-between lg:hidden">
          <LogoImage className="h-8" />
          <ThemeToggle />
        </div>

        {/* top-right controls (desktop) */}
        <div className="absolute right-6 top-6 hidden items-center gap-2 lg:flex">
          <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-background p-0.5">
            {SUPPORTED_LANGUAGES.map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={cn(
                  "h-7 min-w-[28px] rounded-[5px] px-1.5 font-mono text-[12px] transition-colors",
                  lang === l
                    ? "bg-primary-soft text-primary-soft-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm animate-fade-up space-y-7">
            <div className="space-y-1.5">
              <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
                {t({ "zh-TW": "Sign in", "en-US": "Sign in", "zh-CN": "Sign in" })}
              </p>
              <h2 className="font-display text-3xl font-medium tracking-tight">
                {t({ "zh-TW": "歡迎回來", "en-US": "Welcome back", "zh-CN": "欢迎回来" })}
              </h2>
              <p className="text-[13px] text-muted-foreground">
                {t({
                  "zh-TW": "使用你的工作 Email 登入。",
                  "en-US": "Use your work email to continue.",
                  "zh-CN": "使用你的工作 Email 登入。",
                })}
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                login.mutate({ email, password });
              }}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[12px]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[12px]">
                    {t({ "zh-TW": "密碼", "en-US": "Password", "zh-CN": "密码" })}
                  </Label>
                  <button
                    type="button"
                    onClick={() => setLocation("/forgot-password")}
                    className="text-[12px] text-muted-foreground transition-colors hover:text-primary"
                  >
                    {t({ "zh-TW": "忘記密碼？", "en-US": "Forgot?", "zh-CN": "忘记密码？" })}
                  </button>
                </div>
                <PasswordInput
                  id="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <div className="rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_30%,transparent)] bg-destructive-soft px-3 py-2 text-[12px] text-destructive">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={login.isPending} size="lg">
                {login.isPending
                  ? t({ "zh-TW": "登入中…", "en-US": "Signing in…", "zh-CN": "登入中…" })
                  : t({ "zh-TW": "登入", "en-US": "Sign in", "zh-CN": "登入" })}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>

            <div className="relative flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-border" />
              <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                {t({ "zh-TW": "尚未有帳號", "en-US": "New here", "zh-CN": "尚未有帐号" })}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              size="lg"
              onClick={() => setLocation("/register")}
            >
              {t({
                "zh-TW": "建立合作方帳號",
                "en-US": "Create partner account",
                "zh-CN": "建立合作方帐号",
              })}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
